import React from 'react';

/**
 * SegmentedControl — a single-select pill/tab switcher.
 *
 * Props:
 *   value        — currently selected value
 *   onChange     — (value: string) => void
 *   options      — Array of { value, label, icon?, disabled? }
 *   size         — 'sm' | 'md' | 'lg'  (default 'md')
 *   variant      — 'primary' | 'subtle'
 *                  'primary' (default) → active pill is filled primary color (bg-primary text-white)
 *                  'subtle'            → active pill is white with primary text (bg-white text-primary)
 *   disabled     — disables the whole control
 *   fullWidth    — stretches each option to fill equal width
 *   className    — extra class on the outer wrapper
 *   id           — id on the wrapper (useful for Joyride/tour selectors)
 */
const SegmentedControl = ({
    value,
    onChange,
    options = [],
    size = 'md',
    variant = 'primary',
    disabled = false,
    fullWidth = false,
    className = '',
    id,
}) => {
    const sizeConfig = {
        sm: {
            item: 'px-2.5 py-1 text-xs gap-1.5',
            iconOnly: 'p-1.5 text-xs',
        },
        md: {
            item: 'px-3 py-1.5 text-sm gap-2',
            iconOnly: 'p-2 text-sm',
        },
        lg: {
            item: 'px-4 py-2 text-sm gap-2',
            iconOnly: 'p-2.5 text-sm',
        },
    };

    const currentSize = sizeConfig[size] || sizeConfig.md;

    // Active pill styles per variant
    const activeClass = variant === 'subtle' || variant === 'default'
        ? 'bg-white text-primary shadow-xs font-semibold'
        : 'bg-primary text-white shadow-xs font-semibold';

    // Inactive pill styles per variant
    const inactiveClass = 'text-stride-muted hover:text-stride-heading';

    // Container styles per variant
    const containerClass = variant === 'subtle' || variant === 'default'
        ? 'bg-surface border border-border-subtle rounded-lg p-1'
        : 'bg-surface-subtle border border-border rounded-xl p-1';

    return (
        <div
            id={id}
            role="group"
            aria-disabled={disabled}
            className={`inline-flex items-center gap-1 ${containerClass} ${disabled ? 'opacity-60 pointer-events-none' : ''} ${className}`}
        >
            {options.map((opt) => {
                const isActive = opt.value === value;
                const isDisabled = disabled || opt.disabled;
                const hasLabel = Boolean(opt.label && String(opt.label).trim());
                const paddingClass = hasLabel ? currentSize.item : currentSize.iconOnly;

                return (
                    <button
                        key={opt.value}
                        type="button"
                        role="radio"
                        aria-checked={isActive}
                        disabled={isDisabled}
                        onClick={() => !isDisabled && onChange && onChange(opt.value)}
                        className={`
                            inline-flex items-center justify-center rounded-lg font-medium transition-all duration-150 shrink-0
                            ${fullWidth ? 'flex-1' : ''}
                            ${paddingClass}
                            ${isActive ? activeClass : inactiveClass}
                            ${isDisabled && !isActive ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                        `}
                        title={opt.title || opt.label}
                    >
                        {opt.icon && (
                            <span className="inline-flex items-center justify-center shrink-0">
                                {opt.icon}
                            </span>
                        )}
                        {hasLabel && <span>{opt.label}</span>}
                    </button>
                );
            })}
        </div>
    );
};

export default SegmentedControl;

